import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Brain,
  CircleArrowUp,
  CreditCard,
  FileText,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Settings,
  User,
} from 'lucide-react'
import { AppearanceToggle } from '@/components/appearance-toggle'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  getUpgradePlanConfig,
  resolveUpgradePlanPath,
  shouldShowBillingMenuItem,
  shouldShowUpgradeCta,
} from '@/constants/upgrade-plan'
import { useEmployerOrganisationOptional } from '@/context/employer-organisation-context'
import { useFocusStore } from '@/hooks/use-focus-store'
import { useOrganisationBilling } from '@/hooks/use-plans'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { getFocusRoute } from '@/utils/focus-routes'

export type AccountMenuItemsProps = {
  logout: () => void
  onClose?: () => void
  onOpenSettings?: (path: string) => void
  onNavigateToPath?: (path: string) => void
  showSettings?: boolean
  showMyProfile?: boolean
  showGoToApp?: boolean
  goToAppHref?: string
  goToAppLabel?: string
  showUpgrade?: boolean
  showBilling?: boolean
  showMemories?: boolean
  showFeedback?: boolean
  showTheme?: boolean
  showLogout?: boolean
}

export function AccountMenuItems({
  logout,
  onClose,
  onOpenSettings,
  onNavigateToPath,
  showSettings = true,
  showMyProfile = false,
  showGoToApp = false,
  goToAppHref,
  goToAppLabel = 'Go to app',
  showUpgrade = false,
  showBilling = false,
  showMemories = false,
  showFeedback = false,
  showTheme = false,
  showLogout = true,
}: AccountMenuItemsProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { focus } = useFocusStore()
  const employerOrg = useEmployerOrganisationOptional()
  const upgradePlan = getUpgradePlanConfig()
  const isEmployerFocus = focus?.current_focus === 'employer'
  const organisationSlug = isEmployerFocus ? employerOrg?.activeOrganisationSlug ?? undefined : undefined
  const { data: billing } = useOrganisationBilling(showBilling || showUpgrade ? organisationSlug : undefined)
  const planLabel = billing?.display_name?.trim() || null

  const closeAfter = () => {
    onClose?.()
  }

  const navigateTo = (path: string) => {
    closeAfter()
    if (onNavigateToPath) {
      onNavigateToPath(path)
      return
    }
    navigate(path, {
      state: { from: `${location.pathname}${location.search}` },
    })
  }

  const openSettings = (path: string) => {
    closeAfter()
    if (onOpenSettings) {
      onOpenSettings(path)
      return
    }
    navigate(path, {
      state: { from: `${location.pathname}${location.search}` },
    })
  }

  const resolvedGoToAppHref = goToAppHref ?? getFocusRoute(focus?.current_focus ?? null)

  const showUpgradeUi =
    showUpgrade &&
    Boolean(planLabel) &&
    shouldShowUpgradeCta(upgradePlan, {
      isAuthenticated: true,
      isEmployerFocus,
      hasPaidPlan: billing?.has_paid_plan,
    })

  const showBillingMenu =
    showBilling &&
    shouldShowBillingMenuItem(upgradePlan, {
      isAuthenticated: true,
      isEmployerFocus,
      organisationSlug: employerOrg?.activeOrganisationSlug,
      planLabel,
      hasPaidPlan: billing?.has_paid_plan,
      hasUpgradeTarget: Boolean(billing?.upgrade_target),
    })

  const handleUpgradePlan = () => {
    if (!upgradePlan) return
    closeAfter()
    if (upgradePlan.externalUrl) {
      window.open(upgradePlan.externalUrl, '_blank', 'noopener,noreferrer')
      return
    }
    openSettings(resolveUpgradePlanPath(employerOrg?.activeOrganisationSlug))
  }

  return (
    <>
      {showMyProfile ? (
        <DropdownMenuItem onSelect={() => navigateTo('/profile')}>
          <User className="size-4" />
          My profile
        </DropdownMenuItem>
      ) : null}
      {showSettings ? (
        <DropdownMenuItem onSelect={() => openSettings('/settings/account')}>
          <Settings className="size-4" />
          Settings
        </DropdownMenuItem>
      ) : null}
      {showGoToApp ? (
        <DropdownMenuItem onSelect={() => navigateTo(resolvedGoToAppHref)}>
          <LayoutDashboard className="size-4" />
          {goToAppLabel}
        </DropdownMenuItem>
      ) : null}
      {showUpgradeUi ? (
        <DropdownMenuItem onSelect={handleUpgradePlan}>
          <CircleArrowUp className="size-4" />
          <span>{upgradePlan!.ctaButtonLabel}</span>
          <Badge
            variant="outline"
            className="h-5 px-1.5 text-[10px] font-medium leading-none tracking-wide text-muted-foreground"
          >
            {planLabel}
          </Badge>
        </DropdownMenuItem>
      ) : null}
      {showBillingMenu ? (
        <DropdownMenuItem
          onSelect={() =>
            openSettings(resolveUpgradePlanPath(employerOrg?.activeOrganisationSlug))
          }
        >
          <CreditCard className="size-4" />
          <span>{upgradePlan!.label}</span>
          <Badge
            variant="outline"
            className="h-5 px-1.5 text-[10px] font-medium leading-none tracking-wide text-muted-foreground"
          >
            {planLabel}
          </Badge>
        </DropdownMenuItem>
      ) : null}
      {showMemories ? (
        <>
          <DropdownMenuItem
            onSelect={() => {
              closeAfter()
              if (onNavigateToPath) {
                onNavigateToPath('/memories')
                return
              }
              navigateWithThreadPreservation(navigate, '/memories', searchParams)
            }}
          >
            <Brain className="size-4" />
            Memories
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              closeAfter()
              if (onNavigateToPath) {
                onNavigateToPath('/files')
                return
              }
              navigateWithThreadPreservation(navigate, '/files', searchParams)
            }}
          >
            <FileText className="size-4" />
            Files
          </DropdownMenuItem>
        </>
      ) : null}
      {showFeedback ? (
        <DropdownMenuItem
          onSelect={() => {
            closeAfter()
            if (onNavigateToPath) {
              onNavigateToPath('/feedback')
              return
            }
            navigateWithThreadPreservation(navigate, '/feedback', searchParams)
          }}
        >
          <Megaphone className="size-4" />
          Feedback
        </DropdownMenuItem>
      ) : null}
      {showMemories || showFeedback ? <DropdownMenuSeparator /> : null}
      {showTheme ? (
        <DropdownMenuItem
          onSelect={(e) => e.preventDefault()}
          className="flex w-full cursor-default items-center justify-between gap-2 data-[highlighted]:bg-accent focus:bg-accent"
        >
          <span className="text-sm">Theme</span>
          <AppearanceToggle />
        </DropdownMenuItem>
      ) : null}
      {showLogout ? (
        <DropdownMenuItem onSelect={logout}>
          <LogOut className="size-4" />
          Log out
        </DropdownMenuItem>
      ) : null}
    </>
  )
}
